import { useCallback } from 'react';
import { getBlockPlacementEntries, supportsMultiplePlacements } from '../../components/documentSigning/utils';

export const usePlacementHandler = (
  pdfPages,
  signatureWidthPct,
  placementTarget,
  setPlacementTarget,
  textBlocks,
  setTextBlocks,
  setSignaturePosition,
  showToast
) => {
  const handlePlacementClick = useCallback((pageNumber, event) => {
    if (!placementTarget) return;

    const wrapper = event.currentTarget;
    const canvas = wrapper.querySelector('.react-pdf__Page__canvas');

    if (!canvas) {
      const rect = wrapper.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const clamped = (value, max) => Math.min(Math.max(value, 0), max);
      const xPct = clamped(x, rect.width) / rect.width;
      const yPct = clamped(y, rect.height) / rect.height;

      if (placementTarget.type === 'signature') {
        const placement = { pageNumber, xPct, yPct, widthPct: signatureWidthPct };
        setSignaturePosition(placement);
        setPlacementTarget(null);
        showToast('Signature placement saved.', 'success');
        return;
      }
      return;
    }

    const canvasRect = canvas.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();

    const clickX = event.clientX - canvasRect.left;
    const clickY = event.clientY - canvasRect.top;

    const clamped = (value, max) => Math.min(Math.max(value, 0), max);
    const canvasXPct = clamped(clickX, canvasRect.width) / canvasRect.width;
    const canvasYPct = clamped(clickY, canvasRect.height) / canvasRect.height;

    const canvasOffsetX = canvasRect.left - wrapperRect.left;
    const canvasOffsetY = canvasRect.top - wrapperRect.top;
    const wrapperX = canvasOffsetX + (canvasXPct * canvasRect.width);
    const wrapperY = canvasOffsetY + (canvasYPct * canvasRect.height);
    const xPct = wrapperX / wrapperRect.width;
    const yPct = wrapperY / wrapperRect.height;

    const pdfPage = pdfPages[pageNumber - 1];
    let pdfX = null;
    let pdfY = null;
    if (pdfPage) {
      pdfX = canvasXPct * pdfPage.width;
      pdfY = pdfPage.height - (canvasYPct * pdfPage.height);
    }

    if (placementTarget.type === 'signature') {
      const sigWidth = pdfPage ? pdfPage.width * signatureWidthPct : canvasRect.width * signatureWidthPct;
      const sigHeight = pdfPage ? (pdfPage.width * signatureWidthPct * 0.086) : (canvasRect.width * signatureWidthPct * 0.086);

      const adjustedPdfX = pdfPage ? pdfX : pdfX;
      const adjustedPdfY = pdfPage ? pdfY - sigHeight : pdfY;
      const adjustedCanvasXPct = canvasXPct;
      const adjustedCanvasYPct = canvasYPct;

      const placement = {
        pageNumber,
        xPct,
        yPct,
        canvasXPct: adjustedCanvasXPct,
        canvasYPct: adjustedCanvasYPct,
        widthPct: signatureWidthPct,
        pdfX: adjustedPdfX,
        pdfY: adjustedPdfY,
        pdfWidth: pdfPage ? sigWidth : null,
        pdfHeight: pdfPage ? sigHeight : null,
        canvasRect: { left: canvasRect.left, top: canvasRect.top, width: canvasRect.width, height: canvasRect.height },
        wrapperRect: { left: wrapperRect.left, top: wrapperRect.top, width: wrapperRect.width, height: wrapperRect.height },
      };
      setSignaturePosition(placement);
      setPlacementTarget(null);
      showToast('Signature placement saved.', 'success');
      return;
    }

    if (placementTarget.type === 'text' && placementTarget.blockId) {
      const targetBlock = textBlocks.find((entry) => entry.id === placementTarget.blockId);
      if (!targetBlock) {
        setPlacementTarget(null);
        return;
      }
      const capturePerPlacement = Boolean(targetBlock.captureValuePerPlacement);
      const entryValue = capturePerPlacement
        ? placementTarget.value || targetBlock.value
        : targetBlock.value;
      if (capturePerPlacement && !entryValue) {
        showToast(`Select ${targetBlock.label} before placing.`, 'error');
        setPlacementTarget(null);
        return;
      }
      const widthPct =
        typeof targetBlock.widthPct === 'number'
          ? targetBlock.widthPct
          : targetBlock.type === 'date'
            ? 0.22
            : 0.25;

      const textWidth = pdfPage ? pdfPage.width * widthPct : canvasRect.width * widthPct;
      const fontSize = 12;
      const textHeight = fontSize;

      const adjustedPdfX = pdfPage ? pdfX : pdfX;
      const adjustedPdfY = pdfY;
      
      if (pdfPage) {
        pdfX = adjustedPdfX;
        pdfY = adjustedPdfY;
      }

      // Store the click point as the starting position for text (left-aligned)
      // xPct and yPct represent the click point where user clicked - text will start from here
      const adjustedCanvasYPct = canvasYPct;

      const placement = {
        pageNumber,
        xPct, // Click point in wrapper coordinates - text starts from here
        yPct, // Click point in wrapper coordinates
        canvasXPct: canvasXPct, // Click point in canvas coordinates - text starts from here
        canvasYPct: adjustedCanvasYPct,
        widthPct,
        pdfX: pdfX, // Click point for PDF rendering - text starts from here
        pdfY: pdfY, // Click point for PDF rendering
        pdfWidth: pdfPage ? textWidth : null,
        pdfHeight: pdfPage ? textHeight : null,
        canvasRect: { left: canvasRect.left, top: canvasRect.top, width: canvasRect.width, height: canvasRect.height },
        wrapperRect: { left: wrapperRect.left, top: wrapperRect.top, width: wrapperRect.width, height: wrapperRect.height },
      };
      const supportsMultiple = Boolean(
        targetBlock.allowMultiplePlacements || supportsMultiplePlacements(targetBlock)
      );
      const existingEntries = getBlockPlacementEntries(targetBlock);
      const resolvedValue = capturePerPlacement
        ? entryValue
        : existingEntries[0]?.value ?? targetBlock.value ?? entryValue ?? null;
      const nextEntry = { placement, value: resolvedValue };
      const nextEntries = supportsMultiple ? [...existingEntries, nextEntry] : [nextEntry];

      setTextBlocks((prev) =>
        prev.map((block) => {
          if (block.id !== targetBlock.id) return block;
          return {
            ...block,
            placement: nextEntries[0]?.placement || null,
            placements: nextEntries,
          };
        })
      );
      showToast(
        supportsMultiple
          ? `${targetBlock.label} placement added.`
          : `${targetBlock.label} placement saved.`,
        'success'
      );
      setPlacementTarget(null);
      return;
    }
  }, [placementTarget, pdfPages, signatureWidthPct, textBlocks, setPlacementTarget, setSignaturePosition, setTextBlocks, showToast]);

  return { handlePlacementClick };
};
